import React, { useMemo, useState, useRef, useCallback } from 'react';
import { Search, Plus, Minus, X, ArrowUpRight, Mail, LifeBuoy } from 'lucide-react';

const COLORS = {
  ink: '#1a1a1a',
  black: '#000000',
  gold: '#C9A961',
  violet: '#7C3AED',
  bone: '#F5F5F0',
  hairline: '#E8E8E3',
  graphite: '#57574F',
  // #C9A961 is 2.1:1 on bone, so light surfaces use a deepened gold
  goldDeep: '#7F6224',
  mutedOnDark: '#B3B3AD',
};

const HELPDESK = 'helpdesk@skillvend.com';

// The five stages mirror the SkillVender's actual journey, so the numbering
// carries real sequence rather than decoration.
const FAQ_STAGES = [
  { id: 'start', index: '01', label: 'Getting started', blurb: 'Registering, your profile and reaching us' },
  { id: 'work', index: '02', label: 'Finding work', blurb: 'Applying, choosing companies, timings' },
  { id: 'money', index: '03', label: 'Money & payouts', blurb: 'Fees, deductions, how pay is worked out' },
  { id: 'onjob', index: '04', label: 'On the job', blurb: 'Leave, exams, dress code, changes' },
  { id: 'after', index: '05', label: 'After the internship', blurb: 'Certificates and getting hired' },
];

const FAQS = [
  // ---------------------------------------------------------------- start
  {
    id: 'mobile-number',
    stage: 'start',
    q: 'Can I change my mobile number?',
    a: [
      'SkillVend is built around your mobile number. We register you with it, verify you with it, approve you for shifts with it and secure your payouts against it, so every record on the platform points back to that one number. Allowing it to be edited would break that chain, which is why the field is locked once your profile exists.',
      'If your number has changed, delete your profile and create it again with the new number. Same details, same documents, fresh verification. It takes a few minutes.',
      'Do this between engagements, not during one. An internship in progress and the payment records attached to it stay with the profile that earned them.',
    ],
    note: 'Delete your profile from the app, or at skillvend.com/deletemyProfile.',
  },
  {
    id: 'linkedin',
    stage: 'start',
    q: 'Can I use my LinkedIn ID to register on SkillVend?',
    a: [
      'No. There is no LinkedIn sign-in on SkillVend. Every account is created with a mobile number and a one-time password sent to that number.',
      'We place people on real shop floors, hotel floors and event grounds. On the day, the one thing an employer needs is a verified, reachable person, so that is what we build the account on.',
    ],
    related: ['mobile-number'],
  },
  {
    id: 'gmail',
    stage: 'start',
    q: 'Can I use my Gmail ID to register on SkillVend?',
    a: [
      'Not on its own. There is no "sign in with Google" option. The account is always created and verified with your mobile number and an OTP.',
      'You do add an email address while completing your profile, and Gmail is perfectly fine for that. We use it for certificates, confirmations and updates. It simply is not your login.',
    ],
  },
  {
    id: 'college-missing',
    stage: 'start',
    q: 'What if my college is not listed in the drop-down?',
    a: [
      'Type it in. The college field is a search box, not a fixed list. Start typing your college or institution name, and if nothing matches you will see Add "your college name" at the bottom of the results.',
      'Tap that and the college is added to SkillVend and set on your profile immediately. The next student from your campus will find it already there.',
      'Use the full official name rather than the short form, so employers and your college can both recognise it.',
    ],
  },
  {
    id: 'no-college',
    stage: 'start',
    q: 'I have completed my course and am not studying anywhere. What do I do?',
    a: [
      'Nothing extra. College is asked only when you choose Student as your employment status.',
      'Pick the status that describes you now: not employed, employed but available on weekends, or looking for a job and available until placed. The college field drops away and we ask for the course you graduated in instead.',
      'SkillVend is not a students-only platform. If you are 18 or older and have free time, there is work here for you.',
    ],
  },
  {
    id: 'place-of-stay',
    stage: 'start',
    q: 'What counts as "place of stay" in the form?',
    a: [
      'Your place of stay is where you are staying at present. It is the address you would actually leave from to reach a job, not your permanent or home-town address.',
      'If your family home is in Mysuru and you live in a PG in Bengaluru while you study, your place of stay is the PG. Pick the place where you spend most of your time and are genuinely available.',
      'You set it by dropping a pin on the map during onboarding. We sort opportunities by distance from that pin, so an accurate one means a shorter commute and more relevant work. Update it whenever you move.',
    ],
  },
  {
    id: 'edit-details',
    stage: 'start',
    q: 'Can I edit my details after I register?',
    a: [
      'Yes. Your profile is yours to update: name, email, education, place of stay, availability, skills, documents and bank details. Open your profile in the app and edit.',
      'The one exception is your registered mobile number, which stays locked for security reasons.',
      'Keep place of stay and availability current in particular. Those two fields decide which opportunities reach you at all.',
    ],
    related: ['mobile-number', 'place-of-stay'],
  },
  {
    id: 'pan-card',
    stage: 'start',
    q: 'Do I need a PAN card to register?',
    a: [
      'Not to register. You can sign up, complete your profile, get verified and browse opportunities without a PAN card.',
      'You will need it later. Before your first payout you are asked to add your bank account details and your PAN, because the money goes to a bank account and the PAN is required for tax records.',
      'Keep it ready before you start an internship, so a payout is never held up waiting on paperwork.',
    ],
    related: ['deductions'],
  },
  {
    id: 'contact-us',
    stage: 'start',
    q: 'How do I communicate with SkillVend?',
    a: [
      'Use Help and Support in the app for anything tied to your profile, an internship or a payment. It reaches the team with your details already attached, so nobody has to ask you who you are.',
      'For everything else, email ' + HELPDESK + '.',
      'If your question is about a shift happening right now, you are running late, you cannot find the venue, something changed on site, contact us through the app immediately rather than waiting. Same-day problems are handled the same day.',
    ],
  },

  // ----------------------------------------------------------------- work
  {
    id: 'how-to-apply',
    stage: 'work',
    q: 'How do I apply for an internship after I register?',
    a: [
      'Complete your profile and upload your documents first. Verification happens on our side, and an unverified profile cannot be sent to an employer.',
      'Then open the opportunities list. It is sorted by distance from your place of stay, and every listing shows the role, the venue, the dates, the timings and the pay. Open the one you want, read the full brief and apply.',
      'Applying tells the employer you are available for exactly those dates. Once they confirm you, the internship appears in your bookings with the reporting details. Keep notifications on, because confirmations often come quickly.',
    ],
    related: ['place-of-stay', 'timings'],
  },
  {
    id: 'choose-company',
    stage: 'work',
    q: 'Will I get to choose the company where I do my internship?',
    a: [
      'You choose what you apply to. SkillVend does not assign you anywhere. Every opportunity in the app can be read in full before you decide, and you are never obliged to apply to any of it.',
      'What we cannot promise is that one particular company is hiring interns in the week you want one. What is available depends on which employers have live requirements near you.',
      'If you have companies in mind, tell us and we will watch for their openings on your behalf.',
    ],
    related: ['interested-company'],
  },
  {
    id: 'interested-company',
    stage: 'work',
    q: 'If I want to work for a particular company, how do I tell SkillVend?',
    a: [
      'Tell us directly. Send the company name through Help and Support in the app, or email ' + HELPDESK + ' with the company, the branch or area you want, and the kind of role.',
      'We keep the request on file. When that employer posts a requirement that matches your profile and location, you are notified, and where it makes sense we put your profile in front of them.',
      'This works best when your profile is complete and your ratings are good, because that is what we are able to show them.',
    ],
    related: ['job-chances'],
  },
  {
    id: 'internship-and-parttime',
    stage: 'work',
    q: 'Can I take up an internship and part-time work together?',
    a: [
      'Yes, as long as the hours do not clash. An internship carries committed reporting timings; part-time shifts are picked up one at a time. Plenty of SkillVenders intern on weekdays and take weekend shifts.',
      'Be honest with yourself about the load before you accept extra work. Your internship attendance is recorded, and missing internship days to take a part-time shift shows up on your record and in your certificate.',
      'Only accept a shift you can genuinely reach and complete. Cancelling late in the day affects your rating.',
    ],
    related: ['leaves-and-certificate'],
  },
  {
    id: 'unrelated-parttime',
    stage: 'work',
    q: 'Can I do part-time work that is not related to my course?',
    a: [
      'Yes. Part-time work on SkillVend does not have to match what you study. A commerce student can run a cafe counter, an engineering student can work an event, a design student can work a retail floor.',
      'Every one of those roles builds what employers actually screen for: turning up on time, handling customers, holding up through a busy shift and closing out clean.',
      'Internships are the exception. Those are usually mapped to your field, because they carry a certificate that says what you trained in.',
    ],
  },
  {
    id: 'min-duration',
    stage: 'work',
    q: 'If I choose an internship, what is the minimum duration?',
    a: [
      'Each listing states its own duration, and that is the number that counts. Read it before you apply, because applying is a commitment to the full period.',
      'Internships run in blocks of weeks rather than days. An employer gives you a supervisor and training time, so they need you on the floor long enough to become useful. Work measured in single days is listed as part-time, not as an internship.',
      'If you can only commit to a short window right now, take part-time shifts instead. You pick those one at a time.',
    ],
    related: ['cancel-internship'],
  },
  {
    id: 'timings',
    stage: 'work',
    q: 'What will the timings of my internship be?',
    a: [
      'The listing tells you. Reporting time, daily hours and days of the week are all stated before you apply, so there are no surprises on day one.',
      'Timings follow the venue. A cafe opens early, a retail floor runs from mid-morning into the evening, and hotels, multiplexes and events run in shifts that can include weekends, with weekly offs as the venue rosters them.',
      'If the timings clash with your classes, apply somewhere they do not. Reporting late again and again is the quickest way to lose an internship.',
    ],
    related: ['exams'],
  },

  // ---------------------------------------------------------------- money
  {
    id: 'registration-fee',
    stage: 'money',
    q: 'Do I have to pay anything to SkillVend to register?',
    a: [
      'No, and that does not change later. There is no signup fee, no subscription, no charge to complete your profile, no charge to apply and no placement fee.',
      'SkillVend earns from the employers who hire. Never from the people who work.',
      'If anyone asks you for money in exchange for an internship or a shift on SkillVend, do not pay. Report it to ' + HELPDESK + ' immediately.',
    ],
  },
  {
    id: 'deductions',
    stage: 'money',
    q: 'When I get my payout, do I pay anything to SkillVend or is anything deducted?',
    a: [
      'You keep what you earned. SkillVend takes no commission out of your payout and makes no deductions from it.',
      'Your payout is worked out from the days or hours you actually worked, at the rate published in the listing you accepted. That rate is what you are paid.',
      'The only figures that can ever change the amount are statutory deductions an employer is legally required to make, which is also why your bank details and PAN are needed before the first payout.',
    ],
    related: ['pan-card', 'calculation'],
  },
  {
    id: 'calculation',
    stage: 'money',
    q: 'How is my payment calculated?',
    a: [
      'Days present multiplied by the day rate published in the listing. For hourly part-time work, hours worked multiplied by the hourly rate. That is the whole formula.',
      'Attendance is what drives it, so attendance is recorded on the platform. You check in and out on your shift and the employer confirms it.',
      'Days you were absent are not counted. There is no penalty beyond that: an absent day is simply a day that does not appear in the total.',
      'You can see the running total in the app before the payout is released. Check it against your own record and raise anything that looks wrong before it is paid.',
    ],
    related: ['leave', 'deductions'],
  },
  {
    id: 'travel-allowance',
    stage: 'money',
    q: 'Will I get a travel allowance to commute to my internship?',
    a: [
      'Only where the listing says so. A travel or conveyance allowance is not a standard part of every engagement. Some employers include one, and when they do it is stated in the listing alongside the pay.',
      'If it is not stated, plan on covering the commute yourself.',
      'This is exactly why place of stay matters. Opportunities are sorted by how close they are to your pin, so you can choose work that does not spend your earnings on the journey.',
    ],
    related: ['place-of-stay'],
  },

  // ---------------------------------------------------------------- onjob
  {
    id: 'leave',
    stage: 'onjob',
    q: 'Can I apply for leave during my internship?',
    a: [
      'Yes. Interns are not expected to be present without exception. You apply for leave the way an employee would.',
      'Raise it in the app in advance and tell your supervisor at the venue as well. Advance notice is the difference between leave and an absence.',
      'For anything sudden, illness or an emergency, inform the venue and SkillVend as early in the day as you can, before your reporting time wherever that is possible.',
      'Leave days are unpaid, because payment follows attendance.',
    ],
    related: ['calculation', 'leaves-and-certificate'],
  },
  {
    id: 'exams',
    stage: 'onjob',
    q: 'If I have exams, how do I manage my internship?',
    a: [
      'Tell us before you apply rather than afterwards. Exam dates are known months ahead, and once we know them we can match you to an internship whose dates work around them.',
      'If exams fall inside a running internship, raise the leave as early as you can. Employers plan rosters in advance, so a fortnight of notice is usually accepted without difficulty. The same request on the morning of the paper is not.',
      'Your studies come first. An internship should be built around your academic calendar, and we would far rather move the dates than watch you drop out midway.',
    ],
    related: ['leave', 'timings'],
  },
  {
    id: 'leaves-and-certificate',
    stage: 'onjob',
    q: 'If I take a lot of leave, will it affect my certificate?',
    a: [
      'It can. The certificate records what actually happened: the period you worked, your attendance and your supervisor’s assessment. Heavy absence is visible in all three.',
      'Planned leave, taken with notice and approved, is normal and is treated as normal. Repeated unplanned absence is a different matter. It can shorten the period certified, cost you the employer’s recommendation, and if it continues, end the internship with no certificate at all.',
      'Attendance is also the single biggest factor in whether the company offers you a job at the end.',
    ],
    related: ['certificate', 'job-chances'],
  },
  {
    id: 'dress-code',
    stage: 'onjob',
    q: 'What is the dress code during my internship?',
    a: [
      'The venue sets it, not SkillVend, and it is part of the brief you receive before you start. Hotels and multiplexes often provide a uniform, retail floors usually specify formals in set colours, and cafes may allow smart casuals with an apron.',
      'Where nothing specific is stated, clean and ironed formals, closed shoes, hair tied back and minimal accessories will never be wrong.',
      'Grooming standards are enforced strictly in hospitality and food service: closed footwear, trimmed nails, no strong perfume. Reporting out of code can cost you the day’s work.',
    ],
  },
  {
    id: 'change-branch',
    stage: 'onjob',
    q: 'Can I change the branch location during my internship?',
    a: [
      'Ask rather than assume. A branch change is the employer’s decision, and many will consider one for a real reason: you have moved, the commute has become unworkable, your class timings have shifted.',
      'Raise it through Help and Support with the reason and the branch you are asking for. We take it to the employer, and it happens only if they have a vacancy there and agree to the move.',
      'Until the change is confirmed in the app, keep reporting to your current branch.',
    ],
    related: ['place-of-stay'],
  },
  {
    id: 'change-company',
    stage: 'onjob',
    q: 'Can I change my company during the course of my internship?',
    a: [
      'As a rule, no. When you accept an internship the company commits a supervisor, a roster slot and training time to you. Leaving halfway leaves them short, and a certificate cannot be issued for a period you did not complete.',
      'Genuine cases are always heard: a safety concern, harassment, work that is materially different from the brief, or a serious change in your circumstances. Bring any of those to us at once. Do not simply stop attending.',
      'If it is a question of fit rather than safety, the usual path is to finish the term you committed to and apply somewhere else for the next one.',
    ],
    related: ['cancel-internship', 'contact-us'],
  },
  {
    id: 'cancel-internship',
    stage: 'onjob',
    q: 'How do I cancel my internship before the stipulated time is over?',
    a: [
      'Cancel it properly, in the app, before you stop attending. Open the internship in your bookings, raise a cancellation with the reason, and tell your supervisor at the venue as well.',
      'Do it as early as you can. Notice lets the employer backfill the slot; silence leaves a floor short-staffed on a day they were counting on you.',
      'You are paid for the days you worked. An internship that is not completed does not normally earn a certificate.',
      'Cancelling once, with a reason and notice, is understood. Abandoning an internship without a word affects your rating and your access to future opportunities.',
    ],
    related: ['certificate', 'change-company'],
  },

  // ---------------------------------------------------------------- after
  {
    id: 'certificate',
    stage: 'after',
    q: 'At the end of my internship, where do I get the certificate from?',
    a: [
      'SkillVend issues it on behalf of the company you interned with. It is raised at the end of your internship and reaches you through us, so it lands in the app and at the email address on your profile, ready to download and attach anywhere.',
      'It carries the company’s name, your role, the period you worked and your performance, which is why the details on your profile need to be accurate before you start.',
      'Two conditions apply: you complete the period you committed to, and your attendance holds up. Allow a couple of weeks after your last day, and if it has not arrived by then, ask us through Help and Support.',
    ],
    related: ['leaves-and-certificate', 'contact-us'],
  },
  {
    id: 'join-company',
    stage: 'after',
    q: 'Can I join the company where I do my internship?',
    a: [
      'Yes, and it happens regularly. Nothing in SkillVend stops a company from hiring you, and nothing binds you to us if they do. No fee, no notice period, no release required.',
      'It is the most natural way an internship converts. You are already trained on their floor, they have watched you work for weeks, and hiring you costs them nothing in recruitment.',
      'Say it out loud before the internship ends. Tell your supervisor you would like to be considered, and tell us, so we can raise it with the employer at the right moment.',
    ],
    related: ['job-chances'],
  },
  {
    id: 'job-chances',
    stage: 'after',
    q: 'If I intern at a company, what are my chances of getting a job there?',
    a: [
      'We will not put a number on it, and you should be sceptical of anyone who does. It depends on that company’s hiring plans, and on you.',
      'What we can tell you is what moves the odds: attendance and punctuality first, then your supervisor’s assessment, your ratings on the platform, and whether you took on more than the minimum asked of you.',
      'Interning is the strongest position to be in. Faced with an unknown CV and someone who has already worked their floor for two months, a manager picks the person they have seen. And when that company is not hiring, the rated record you built on SkillVend goes with you to the next one.',
    ],
    related: ['join-company', 'leaves-and-certificate'],
  },
];

const plainText = (faq) => faq.a.join(' ') + (faq.note ? ' ' + faq.note : '');

// FAQPage structured data, generated from the same source as the visible list
const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: plainText(faq) },
  })),
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Terms match from the start of a word, so "pan" finds PAN card and not "company"
const toTerms = (query) => query.trim().toLowerCase().split(/\s+/).filter((t) => t.length >= 2);

const Highlight = ({ text, terms }) => {
  if (!terms.length) return text;
  const pattern = new RegExp('(' + terms.map((t) => '\\b' + escapeRegExp(t)).join('|') + ')', 'ig');
  const isTerm = new RegExp('^(' + terms.map(escapeRegExp).join('|') + ')$', 'i');
  return text.split(pattern).map((part, i) =>
    isTerm.test(part) ? (
      <mark key={i} className="bg-[#C9A961]/30 text-inherit px-0.5">{part}</mark>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const FaqSection = () => {
  const [stage, setStage] = useState('start');
  const [query, setQuery] = useState('');
  const [openIds, setOpenIds] = useState(() => new Set());
  const itemRefs = useRef({});

  const terms = useMemo(() => toTerms(query), [query]);
  const searching = terms.length > 0;

  const matches = useMemo(() => {
    if (!terms.length) return FAQS;
    const patterns = terms.map((t) => new RegExp('\\b' + escapeRegExp(t), 'i'));
    const hits = FAQS.filter((faq) => {
      const haystack = faq.q + ' ' + plainText(faq);
      return patterns.every((re) => re.test(haystack));
    });
    // Questions that match the terms outrank answers that merely mention them
    const inQuestion = (faq) => patterns.every((re) => re.test(faq.q));
    return [...hits.filter(inQuestion), ...hits.filter((faq) => !inQuestion(faq))];
  }, [terms]);

  const visible = searching ? matches : matches.filter((faq) => faq.stage === stage);

  const countFor = useCallback(
    (stageId) => matches.filter((faq) => faq.stage === stageId).length,
    [matches]
  );

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const jumpTo = (id) => {
    const target = FAQS.find((faq) => faq.id === id);
    if (!target) return;
    setQuery('');
    setStage(target.stage);
    setOpenIds((prev) => new Set(prev).add(id));
    window.requestAnimationFrame(() => {
      const node = itemRefs.current[id];
      if (node) node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const allVisibleOpen = visible.length > 0 && visible.every((faq) => openIds.has(faq.id));

  const toggleAllVisible = () => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      visible.forEach((faq) => (allVisibleOpen ? next.delete(faq.id) : next.add(faq.id)));
      return next;
    });
  };

  const activeStage = FAQ_STAGES.find((s) => s.id === stage);

  return (
    <section id="faq" className="anchor-target" style={{ backgroundColor: COLORS.bone }}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(FAQ_SCHEMA) }}
      />

      {/* Masthead band */}
      <div className="bg-black text-white border-t border-b" style={{ borderColor: COLORS.gold }}>
        <div className="container mx-auto px-6 max-w-7xl py-11 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
            <div className="lg:col-span-7">
              <span className="font-ledger text-[0.65rem] tracking-[0.35em] uppercase" style={{ color: COLORS.gold }}>
                Answers / {FAQS.length}
              </span>
              <h2 className="mt-5 text-4xl md:text-6xl font-extralight leading-[1.05] tracking-tight">
                Everything you ask
                <span className="block mt-2" style={{ color: COLORS.gold }}>before your first shift.</span>
              </h2>
              <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-gray-400 max-w-xl">
                Straight answers on registration, money, leave and certificates. Written for
                SkillVenders, in the order you will need them.
              </p>
            </div>

            <div className="lg:col-span-5 w-full">
              <label htmlFor="faq-search" className="font-ledger block text-[0.6rem] tracking-[0.3em] uppercase text-[#B3B3AD] mb-3">
                Search the answers
              </label>
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B3B3AD] pointer-events-none" />
                <input
                  id="faq-search"
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="PAN card, leave, payout, certificate..."
                  className="w-full bg-transparent border border-[#5A5A55] focus:border-[#C9A961] text-white placeholder-[#8E8E88] font-light text-sm py-4 pl-11 pr-11 transition-colors duration-150"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setQuery('')}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-[#B3B3AD] hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="font-ledger mt-3 text-[0.65rem] tracking-widest uppercase text-[#B3B3AD]" aria-live="polite">
                {searching
                  ? matches.length + (matches.length === 1 ? ' answer matches' : ' answers match')
                  : 'Five stages, from signing up to your certificate'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="container mx-auto px-6 max-w-7xl py-11 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">

          {/* Stage rail */}
          <nav aria-label="FAQ stages" className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <p className="font-ledger hidden lg:block text-[0.65rem] tracking-[0.2em] uppercase text-[#57574F] mb-5">
                The journey
              </p>

              {/* Mobile: horizontal chips */}
              <div className="lg:hidden -mx-6 px-6 overflow-x-auto no-scrollbar">
                <div className="flex gap-2 w-max pb-1">
                  {FAQ_STAGES.map((s) => {
                    const active = !searching && s.id === stage;
                    return (
                      <button
                        key={s.id}
                        onClick={() => { setQuery(''); setStage(s.id); }}
                        className="flex items-center gap-2 whitespace-nowrap border px-4 py-2.5 text-xs font-light tracking-wide transition-colors duration-150"
                        style={{
                          backgroundColor: active ? COLORS.ink : 'transparent',
                          color: active ? COLORS.gold : COLORS.ink,
                          borderColor: active ? COLORS.ink : COLORS.hairline,
                        }}
                      >
                        <span className="font-ledger text-[0.65rem] opacity-80">{s.index}</span>
                        {s.label}
                        <span className="font-ledger text-[0.65rem] opacity-75">{countFor(s.id)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Desktop: ledger rail */}
              <ul className="hidden lg:block border-t" style={{ borderColor: COLORS.hairline }}>
                {FAQ_STAGES.map((s) => {
                  const active = !searching && s.id === stage;
                  return (
                    <li key={s.id} className="border-b" style={{ borderColor: COLORS.hairline }}>
                      <button
                        onClick={() => { setQuery(''); setStage(s.id); }}
                        aria-current={active ? 'true' : undefined}
                        className="group w-full text-left py-5 pl-5 pr-3 relative transition-colors duration-150 hover:bg-white"
                      >
                        <span
                          className="absolute left-0 top-0 bottom-0 w-[3px] transition-all duration-300"
                          style={{ backgroundColor: active ? COLORS.gold : 'transparent' }}
                        />
                        <span className="flex items-baseline justify-between gap-4">
                          <span className="flex items-baseline gap-4">
                            <span
                              className="font-ledger text-[0.7rem] tracking-widest"
                              style={{ color: active ? COLORS.goldDeep : '#6B6B66' }}
                            >
                              {s.index}
                            </span>
                            <span>
                              <span
                                className="block text-base font-light tracking-wide"
                                style={{ color: active ? COLORS.ink : '#4A4A46' }}
                              >
                                {s.label}
                              </span>
                              <span className="block text-xs font-light mt-1" style={{ color: COLORS.graphite }}>
                                {s.blurb}
                              </span>
                            </span>
                          </span>
                          <span className="font-ledger text-[0.7rem]" style={{ color: '#6B6B66' }}>
                            {countFor(s.id)}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>

              {/* Support card */}
              <div className="hidden lg:block mt-10 border p-6 bg-white" style={{ borderColor: COLORS.hairline }}>
                <LifeBuoy className="w-5 h-5 mb-4" style={{ color: COLORS.violet }} />
                <p className="text-sm font-light leading-relaxed" style={{ color: COLORS.ink }}>
                  Question not here? Ask through Help and Support in the app, or write to us.
                </p>
                <a
                  href={'mailto:' + HELPDESK}
                  className="mt-4 inline-flex items-center gap-2 text-xs font-light tracking-wide border-b pb-0.5 transition-colors hover:border-black"
                  style={{ color: COLORS.ink, borderColor: COLORS.gold }}
                >
                  {HELPDESK}
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </nav>

          {/* Answer list */}
          <div className="lg:col-span-8">
            <div className="flex items-baseline justify-between gap-4 mb-6 pb-4 border-b" style={{ borderColor: COLORS.hairline }}>
              <h3 className="font-ledger text-[0.65rem] tracking-[0.3em] uppercase" style={{ color: COLORS.graphite }}>
                {searching
                  ? 'Results for "' + query.trim() + '"'
                  : activeStage.index + ' · ' + activeStage.label}
              </h3>
              {visible.length > 0 && (
                <button
                  onClick={toggleAllVisible}
                  className="text-[0.65rem] font-light tracking-widest uppercase transition-colors hover:text-black"
                  style={{ color: COLORS.graphite }}
                >
                  {allVisibleOpen ? 'Collapse all' : 'Expand all'}
                </button>
              )}
            </div>

            {visible.length === 0 ? (
              <div className="border p-10 text-center bg-white" style={{ borderColor: COLORS.hairline }}>
                <p className="text-lg font-light" style={{ color: COLORS.ink }}>
                  Nothing matches &ldquo;{query.trim()}&rdquo;.
                </p>
                <p className="text-sm font-light mt-3" style={{ color: COLORS.graphite }}>
                  Try a shorter word, or ask us and we will answer it directly.
                </p>
                <a
                  href={'mailto:' + HELPDESK}
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-xs font-light tracking-widest uppercase transition-colors hover:bg-[#C9A961] hover:text-black"
                >
                  <Mail className="w-4 h-4" />
                  Email the helpdesk
                </a>
              </div>
            ) : (
              <ul className="border-t" style={{ borderColor: COLORS.hairline }}>
                {visible.map((faq, i) => {
                  const open = openIds.has(faq.id);
                  const stageOf = FAQ_STAGES.find((s) => s.id === faq.stage);
                  return (
                    <li
                      key={faq.id}
                      ref={(node) => { itemRefs.current[faq.id] = node; }}
                      className="border-b transition-colors duration-150"
                      style={{ borderColor: COLORS.hairline, backgroundColor: open ? '#FFFFFF' : 'transparent' }}
                    >
                      <h4>
                        <button
                          onClick={() => toggle(faq.id)}
                          aria-expanded={open}
                          aria-controls={'faq-panel-' + faq.id}
                          className="group w-full flex items-start gap-4 md:gap-6 text-left py-6 px-4 md:px-6"
                        >
                          <span
                            className="font-ledger text-[0.7rem] pt-1.5 tracking-widest tabular-nums"
                            style={{ color: open ? COLORS.goldDeep : '#6B6B66' }}
                          >
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span className="flex-1">
                            <span
                              className="block text-base md:text-lg font-light leading-snug tracking-wide transition-colors duration-150"
                              style={{ color: COLORS.ink }}
                            >
                              <Highlight text={faq.q} terms={terms} />
                            </span>
                            {searching && (
                              <span className="font-ledger block mt-2 text-[0.65rem] tracking-[0.18em] uppercase" style={{ color: COLORS.graphite }}>
                                {stageOf.index} {stageOf.label}
                              </span>
                            )}
                          </span>
                          <span
                            className="mt-0.5 flex-shrink-0 w-8 h-8 border flex items-center justify-center transition-all duration-300 group-hover:border-black"
                            style={{
                              borderColor: open ? COLORS.ink : COLORS.hairline,
                              backgroundColor: open ? COLORS.ink : 'transparent',
                            }}
                          >
                            {open
                              ? <Minus className="w-4 h-4" style={{ color: COLORS.gold }} />
                              : <Plus className="w-4 h-4" style={{ color: COLORS.ink }} />}
                          </span>
                        </button>
                      </h4>

                      <div
                        id={'faq-panel-' + faq.id}
                        className="faq-panel"
                        style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
                      >
                        <div
                          className="overflow-hidden"
                          style={{
                            visibility: open ? 'visible' : 'hidden',
                            transition: 'visibility 0s linear ' + (open ? '0s' : '380ms'),
                          }}
                        >
                          <div className="pb-8 pt-1 pl-4 pr-4 md:pl-[4.5rem] md:pr-6 space-y-4 max-w-2xl">
                            {faq.a.map((para, pi) => (
                              <p key={pi} className="text-sm md:text-[0.95rem] font-light leading-[1.85]" style={{ color: '#4A4A46' }}>
                                {para}
                              </p>
                            ))}

                            {faq.note && (
                              <p
                                className="font-ledger text-xs font-light leading-relaxed border-l-2 pl-4 py-1"
                                style={{ borderColor: COLORS.goldDeep, color: COLORS.graphite }}
                              >
                                {faq.note}
                              </p>
                            )}

                            {faq.related && faq.related.length > 0 && (
                              <div className="pt-2 flex flex-wrap gap-x-5 gap-y-2">
                                {faq.related.map((rid) => {
                                  const rel = FAQS.find((f) => f.id === rid);
                                  if (!rel) return null;
                                  return (
                                    <button
                                      key={rid}
                                      onClick={() => jumpTo(rid)}
                                      className="inline-flex items-center gap-1.5 text-xs font-light text-left transition-colors hover:text-black"
                                      style={{ color: COLORS.violet }}
                                    >
                                      {rel.q}
                                      <ArrowUpRight className="w-3 h-3 flex-shrink-0" />
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Mobile support card */}
            <div className="lg:hidden mt-10 border p-6 bg-white" style={{ borderColor: COLORS.hairline }}>
              <LifeBuoy className="w-5 h-5 mb-4" style={{ color: COLORS.violet }} />
              <p className="text-sm font-light leading-relaxed" style={{ color: COLORS.ink }}>
                Question not here? Ask through Help and Support in the app, or write to us.
              </p>
              <a
                href={'mailto:' + HELPDESK}
                className="mt-4 inline-flex items-center gap-2 text-xs font-light tracking-wide border-b pb-0.5"
                style={{ color: COLORS.ink, borderColor: COLORS.gold }}
              >
                {HELPDESK}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
